import React, { useState, useEffect } from 'react';
import { useGetSubscriptionHistoryQuery, useGetUsersQuery, useGetPackagesQuery } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import Pagination from '../../../components/Pagination';

// Format date as "d M, Y, h:i A" (e.g., "05 Dec 2025, 05:52 PM")
const formatDateTime = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = date.getDate().toString().padStart(2, '0');
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = (hours % 12 || 12).toString().padStart(2, '0');
  return `${day} ${month} ${year}, ${displayHours}:${minutes} ${ampm}`;
};

const AdminSubscriptionHistory: React.FC = () => {
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedPackageId, setSelectedPackageId] = useState('');
  const [page, setPage] = useState(1);
  
  const { data, isLoading, error } = useGetSubscriptionHistoryQuery({ 
    user_id: selectedUserId || undefined,
    package_id: selectedPackageId || undefined,
    page 
  });
  
  const { data: usersData } = useGetUsersQuery({ per_page: 1000 });
  const { data: packagesData } = useGetPackagesQuery({ per_page: 1000 });

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [selectedUserId, selectedPackageId]);

  if (isLoading) {
    return <PageLoader />;
  }

  if (error) {
    return <div className="alert alert-danger m-5">Error loading subscription history</div>;
  }

  const subscriptions = data?.data || { data: [], current_page: 1, last_page: 1, total: 0, per_page: 15 };
  const users = usersData?.data?.data || [];
  const packages = packagesData?.data?.data || [];

  const getStatusBadge = (expiredAt: string | null) => {
    if (!expiredAt) return { text: 'Active', class: 'success' };
    const expired = new Date(expiredAt) < new Date();
    return expired ? { text: 'Expired', class: 'danger' } : { text: 'Active', class: 'success' };
  };

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">Subscription History</h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" className="text-muted text-hover-primary">Home</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">Subscriptions</li>
              </ul>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-header border-0 pt-6">
                <div className="card-title">
                  <form onSubmit={(e) => { e.preventDefault(); }} className="d-flex align-items-center gap-3">
                    <select
                      className="form-select form-select-solid w-200px"
                      value={selectedUserId}
                      onChange={(e) => setSelectedUserId(e.target.value)}
                    >
                      <option value="">Select User</option>
                      {users.map((user: any) => (
                        <option key={user.id} value={user.id}>{user.name || user.email}</option>
                      ))}
                    </select>
                    <select
                      className="form-select form-select-solid w-200px"
                      value={selectedPackageId}
                      onChange={(e) => setSelectedPackageId(e.target.value)}
                    >
                      <option value="">Select Package</option>
                      {packages.map((pkg: any) => (
                        <option key={pkg.id} value={pkg.id}>{pkg.name}</option>
                      ))}
                    </select>
                    <button type="submit" className="btn btn-base">Filter</button>
                    {(selectedUserId || selectedPackageId) && (
                      <a href="#" onClick={(e) => { e.preventDefault(); setSelectedUserId(''); setSelectedPackageId(''); }} className="btn btn-sm btn-light-primary">
                        Clear
                      </a>
                    )}
                  </form>
                </div>
              </div>
              <div className="card-body pt-0">
                <div className="table-responsive">
                  <table className="table align-middle table-row-dashed fs-6 gy-5">
                    <thead>
                      <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                        <th>#</th>
                        <th>USER</th>
                        <th>PACKAGE</th>
                        <th>PURCHASE/RENEW DATE</th>
                        <th>EXPIRED DATE</th>
                        <th>IS AUTO RENEWED</th>
                        <th>STATUS</th>
                      </tr>
                    </thead>
                    <tbody className="fw-semibold text-gray-600">
                      {subscriptions.data.map((subscription: any, index: number) => {
                        const status = getStatusBadge(subscription.expired_at);
                        return (
                          <tr key={subscription.id}>
                            <td>{(subscriptions.current_page - 1) * 15 + index + 1}</td>
                            <td>{subscription.user?.name || subscription.user?.email || subscription.user?.username || 'N/A'}</td>
                            <td>{subscription.package?.name || 'N/A'}</td>
                            <td>{formatDateTime(subscription.created_at || subscription.buy_at)}</td>
                            <td>{formatDateTime(subscription.expired_at)}</td>
                            <td>
                              <span className={`badge badge-light-${subscription.is_auto_renewed === 1 ? 'success' : 'danger'}`}>
                                {subscription.is_auto_renewed === 1 ? 'Yes' : 'No'}
                              </span>
                            </td>
                            <td>
                              <span className={`badge badge-light-${status.class}`}>
                                {status.text}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {subscriptions.data.length > 0 && (
                  <div className="card-footer border-0 pt-6">
                    <Pagination
                      currentPage={subscriptions.current_page}
                      lastPage={subscriptions.last_page}
                      total={subscriptions.total || 0}
                      perPage={subscriptions.per_page || 15}
                      onPageChange={(newPage) => setPage(newPage)}
                      showTotal={true}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSubscriptionHistory;
